/**
 * WarrantVsUnderlyingChart — Round P140·6 (Feb 2026)
 *
 * Compact 90-day comparison chart rendered inside the Stock Modal's
 * Insights tab whenever the current ticker is a warrant. Both series
 * are normalized to base 100 at the earliest shared date so the
 * warrant's smaller absolute price doesn't get squished against the
 * underlying's chart.
 *
 * Chart uses recharts — same lib already loaded across the app.
 *
 * Silent when the ticker isn't a warrant OR when Massive returns
 * empty series (e.g. warrant recently listed with fewer than 7 days
 * of history).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, Legend, ResponsiveContainer } from 'recharts';
import TapTooltipChart from '@/components/TapTooltipChart';
import { TrendingUp, TrendingDown, HelpCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WarrantVsUnderlyingChart({ ticker, days = 90 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Round P142·2 (Feb 2026) — Chart explainer modal state.
  const [helpOpen, setHelpOpen] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setLoading(true); setErr(false);
    axios
      .get(`${API}/warrants/${encodeURIComponent(ticker)}/compare`, { params: { days } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) { setData(null); setErr(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker, days]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border/40 p-3 h-40 flex items-center justify-center text-xs text-muted-foreground">
        Loading warrant vs. underlying comparison…
      </div>
    );
  }
  if (err || !data || !data.series?.length) return null;

  const { warrant_ticker, underlying_ticker, leverage_ratio, series } = data;
  const last = series[series.length - 1];
  const w_pct = last.warrant_indexed != null ? last.warrant_indexed - 100 : null;
  const u_pct = last.underlying_indexed != null ? last.underlying_indexed - 100 : null;
  const leveragePositive = leverage_ratio != null && leverage_ratio > 0;

  return (
    <div
      className="rounded-xl border border-indigo-400/50 bg-indigo-50/30 dark:bg-indigo-950/25 p-3 sm:p-4"
      data-testid="warrant-vs-underlying-chart"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div>
          <h3 className="text-sm font-bold text-foreground flex items-center gap-1.5">
            {leverage_ratio != null && Math.abs(leverage_ratio) >= 1
              ? <TrendingUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              : <TrendingDown className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
            Warrant vs. Underlying
            <span className="text-[10px] font-mono text-muted-foreground ml-1">({days}d)</span>
            {/* Round P142·2 (Feb 2026) — Chart explainer icon. Click
                opens a modal breaking down each metric shown above
                (leverage, warrant %, underlying %) plus what the two
                lines mean and how convergence/divergence signal
                relative strength. */}
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="ml-1 inline-flex items-center justify-center rounded-full text-indigo-600/70 dark:text-indigo-400/70 hover:text-indigo-700 dark:hover:text-indigo-300 focus:outline-none focus:ring-2 focus:ring-violet-400"
              aria-label="How to read this chart"
              title="How to read this chart"
              data-testid="warrant-vs-underlying-help"
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </h3>
        </div>
        {leverage_ratio != null && (
          <div
            className={`text-xs font-bold ${
              leveragePositive ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'
            }`}
            data-testid="warrant-vs-underlying-leverage"
            title="Ratio of warrant % change to underlying % change over the window"
          >
            Leverage: {leverage_ratio.toFixed(2)}×
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2 text-[11px]">
        <div>
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{warrant_ticker}</div>
          <div className={`font-mono font-bold ${w_pct != null && w_pct >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`} data-testid="warrant-vs-warrant-pct">
            {w_pct == null ? '—' : `${w_pct >= 0 ? '+' : ''}${w_pct.toFixed(2)}%`}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{underlying_ticker || 'Underlying'}</div>
          <div className={`font-mono font-bold ${u_pct != null && u_pct >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`} data-testid="warrant-vs-under-pct">
            {u_pct == null ? '—' : `${u_pct >= 0 ? '+' : ''}${u_pct.toFixed(2)}%`}
          </div>
        </div>
      </div>

      <TapTooltipChart className="h-full">

      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={series} margin={{ top: 5, right: 12, bottom: 4, left: 0 }}>
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10 }}
            interval={Math.max(1, Math.floor(series.length / 6))}
          />
          <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
          <Tooltip
            formatter={(value, name) => [value == null ? '—' : `${value.toFixed(2)}`, name]}
            labelFormatter={(l) => l}
          />
          <ReferenceLine y={100} strokeDasharray="3 3" stroke="#94a3b8" />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
          <Line
            type="monotone"
            dataKey="warrant_indexed"
            name={warrant_ticker}
            stroke="#dc6dd2"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="underlying_indexed"
            name={underlying_ticker || 'Underlying'}
            stroke="#6d8ce6"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      </TapTooltipChart>

      <p className="mt-2 text-[10px] text-muted-foreground italic leading-snug">
        Both series indexed to 100 at day 1. A leverage ratio {'>'}1× means the warrant amplified the underlying&rsquo;s move; {'<'}0 means they diverged.
      </p>

      {/* Round P142·2 (Feb 2026) — Chart explainer dialog. */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-md" data-testid="warrant-vs-underlying-help-dialog">
          <DialogHeader>
            <DialogTitle>How to read this chart</DialogTitle>
            <DialogDescription>
              A quick decoder for the three numbers on top and the two lines below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md bg-muted/40 p-3 text-xs leading-relaxed">
              <div className="font-semibold text-fuchsia-700 dark:text-fuchsia-400 mb-1">
                {warrant_ticker} {w_pct != null ? `${w_pct >= 0 ? '+' : ''}${w_pct.toFixed(2)}%` : ''}
              </div>
              The <span className="font-mono">warrant&rsquo;s</span> total price change over the {days}-day window
              (first close to last close). Warrants swing far more than the underlying because they&rsquo;re
              leveraged optionality — a small move in the stock creates a large move in the warrant.
            </div>

            <div className="rounded-md bg-muted/40 p-3 text-xs leading-relaxed">
              <div className="font-semibold text-indigo-700 dark:text-indigo-400 mb-1">
                {underlying_ticker || 'Underlying'} {u_pct != null ? `${u_pct >= 0 ? '+' : ''}${u_pct.toFixed(2)}%` : ''}
              </div>
              The <span className="font-mono">underlying stock&rsquo;s</span> total price change over the same
              window. This is what the warrant tracks — if the common stock goes nowhere, the warrant
              usually decays. If the common rises above the strike, the warrant rockets.
            </div>

            <div className="rounded-md bg-muted/40 p-3 text-xs leading-relaxed">
              <div className="font-semibold text-emerald-700 dark:text-emerald-400 mb-1">
                Leverage: {leverage_ratio != null ? `${leverage_ratio.toFixed(2)}×` : '—'}
              </div>
              Warrant % divided by underlying %. A leverage of <span className="font-mono">20.53×</span>
              means for every 1% the underlying moved, the warrant moved ~20.5%. Above 1× = amplified
              (working as intended). Below 0× = they moved in opposite directions (rare — usually a data
              gap or an 8-K redemption event).
            </div>

            <div className="rounded-md bg-muted/40 p-3 text-xs leading-relaxed">
              <div className="font-semibold mb-1">The two lines</div>
              <p className="mb-1">
                Both lines start at <span className="font-mono">100</span> on day 1 so you can compare
                percentage moves side-by-side regardless of the actual dollar prices.
              </p>
              <p className="mb-1">
                <span className="text-fuchsia-700 dark:text-fuchsia-400 font-semibold">Pink line = warrant</span>.
                Steeper swings, more volatile. This is your P/L profile.
              </p>
              <p className="mb-1">
                <span className="text-indigo-700 dark:text-indigo-400 font-semibold">Blue line = underlying</span>.
                Smoother, closer to 100. This is the fundamental driver.
              </p>
              <p>
                <span className="font-semibold">Convergence</span> (lines meeting) = warrant tracking the
                stock 1-for-1 (leverage ≈ 1×). <span className="font-semibold">Divergence</span> (lines
                fanning out) = warrant either amplifying gains OR bleeding time value while the stock
                stays flat.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
